import { dec, toDecimalString } from '../quantity/decimal.js';
import { DEFAULT_UNITS, getUnit } from './catalog.js';
export class UnitConversionError extends Error {
    code;
    constructor(message, code) {
        super(message);
        this.code = code;
        this.name = 'UnitConversionError';
    }
}
/**
 * Convert within a dimension. Mass <-> volume conversion is only performed when a
 * density for the ingredient is supplied; the system never assumes 1 ml = 1 g.
 */
/**
 * Units that mean something else in a British recipe: the catalog holds the US values, a
 * recipe written in the UK system is read with these (an imperial pint is 568 ml, not 473 ml;
 * the cup of British and Australian recipes is the metric 250 ml cup).
 */
const UK_BASE_FACTORS = {
    floz: '28.4130625',
    pint: '568.26125',
    quart: '1136.5225',
    gallon: '4546.09',
    cup: '250',
};
/** Factor of a unit to the base unit of its dimension, as meant in the system a recipe was written in. */
export function baseFactorIn(unit, sourceSystem) {
    return (sourceSystem === 'UK' ? UK_BASE_FACTORS[unit.code] : undefined) ?? unit.baseFactor;
}
export function convertQuantity(quantity, fromCode, toCode, options = {}) {
    if (fromCode === toCode)
        return { quantity, usedDensity: false };
    const found = getUnit(fromCode);
    const to = getUnit(toCode);
    if (!found || !to)
        throw new UnitConversionError(`Unknown unit ${!found ? fromCode : toCode}`, 'UNKNOWN_UNIT');
    // Only the source is read with the recipe's system; the target is always the catalog unit.
    const from = { ...found, baseFactor: baseFactorIn(found, options.sourceSystem) };
    if (from.dimension === to.dimension && from.dimension !== 'OTHER') {
        const base = dec(quantity).times(from.baseFactor);
        return { quantity: toDecimalString(base.div(to.baseFactor)), usedDensity: false };
    }
    const massVolume = (from.dimension === 'MASS' && to.dimension === 'VOLUME') ||
        (from.dimension === 'VOLUME' && to.dimension === 'MASS');
    if (massVolume) {
        if (!options.density) {
            throw new UnitConversionError('Mass/volume conversion requires an ingredient density', 'DENSITY_REQUIRED');
        }
        const density = dec(options.density);
        if (from.dimension === 'MASS') {
            const grams = dec(quantity).times(from.baseFactor);
            const ml = grams.div(density);
            return { quantity: toDecimalString(ml.div(to.baseFactor)), usedDensity: true };
        }
        const ml = dec(quantity).times(from.baseFactor);
        const grams = ml.times(density);
        return { quantity: toDecimalString(grams.div(to.baseFactor)), usedDensity: true };
    }
    throw new UnitConversionError(`Cannot convert ${fromCode} to ${toCode}`, 'INCOMPATIBLE_DIMENSIONS');
}
const PREFERRED = {
    METRIC: { MASS: ['g', 'kg'], VOLUME: ['ml', 'l'] },
    US: { MASS: ['oz', 'lb'], VOLUME: ['tsp', 'tbsp', 'floz', 'cup', 'quart'] },
    // British cooks weigh in grams and measure in millilitres; what is British is how a pint or a
    // fluid ounce in one of their recipes is read (UK_BASE_FACTORS).
    UK: { MASS: ['g', 'kg'], VOLUME: ['ml', 'l'] },
};
/** The unit system a measurement system displays: the UK system shows metric units. */
const DISPLAY_UNIT_SYSTEM = {
    METRIC: 'METRIC',
    US: 'US',
    UK: 'METRIC',
};
/** Whether a unit has to be converted for a reader, given the system the recipe was written in. */
export function needsConversion(unitCode, target, sourceSystem) {
    const unit = getUnit(unitCode);
    if (!unit || unit.dimension === 'OTHER' || unit.dimension === 'COUNT')
        return false;
    // A British cup or pint is not the catalog's: even "the same" unit needs another number.
    if (sourceSystem === 'UK' && target !== 'UK' && unit.code in UK_BASE_FACTORS)
        return true;
    if (unit.system === 'UNIVERSAL')
        return false;
    return unit.system !== DISPLAY_UNIT_SYSTEM[target];
}
/**
 * Pick a readable unit in the target system for a quantity. Universal units
 * (tsp, tbsp, piece) are left alone. Returns the input unchanged when no
 * conversion is appropriate.
 */
export function convertToSystem(quantity, unitCode, system, options = {}) {
    const found = getUnit(unitCode);
    if (!found || !needsConversion(unitCode, system, options.sourceSystem)) {
        return { quantity, unitCode, converted: false };
    }
    const unit = { ...found, baseFactor: baseFactorIn(found, options.sourceSystem) };
    const candidates = PREFERRED[system][unit.dimension];
    if (!candidates || candidates.length === 0)
        return { quantity, unitCode, converted: false };
    const base = dec(quantity).times(unit.baseFactor);
    let best = null;
    for (const code of candidates) {
        const target = getUnit(code);
        const value = base.div(target.baseFactor);
        // Prefer the largest unit that still yields at least 1 (or 0.25 for spoons/cups).
        // Litres and kilograms only from one upwards: a cook reads "575 ml", not "0.55 l".
        const floor = code === 'tsp' || code === 'tbsp'
            ? dec('0.25')
            : code === 'l' || code === 'kg'
                ? dec('1')
                : dec('0.5');
        if (value.gte(floor))
            best = { code, value };
    }
    if (!best) {
        const target = getUnit(candidates[0]);
        best = { code: target.code, value: base.div(target.baseFactor) };
    }
    return {
        quantity: toDecimalString(best.value),
        unitCode: best.code,
        converted: best.code !== unitCode || toDecimalString(best.value) !== quantity,
    };
}
export function unitsInDimension(dimension) {
    return DEFAULT_UNITS.filter((unit) => unit.dimension === dimension);
}
export function areUnitsCompatible(a, b) {
    if (a === b)
        return true;
    if (a === null || b === null)
        return false;
    const ua = getUnit(a);
    const ub = getUnit(b);
    if (!ua || !ub)
        return false;
    return ua.dimension === ub.dimension && ua.dimension !== 'OTHER';
}
//# sourceMappingURL=conversion.js.map